import { useCallback, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import DockedActionGateModal from "components/docking/DockedActionGateModal";
import { useDocking } from "providers/DockingProvider";
import { useDockingUiVisible } from "lib/hooks/useDockingUiVisible";

type PendingGate = {
    body: string;
    proceed: () => void;
};

/**
 * Gate actions that may stand/drive/load-map while docked.
 * Sync: runWithDockedGate(fn). Async: await runWithDockedGateAsync(() => work()).
 * Always render {dockedGateModal} next to the calling UI.
 */
export function useDockedActionGate() {
    const { t } = useTranslation("control");
    const dockingUiVisible = useDockingUiVisible();
    const { isDocked, triggerDocking, actionStatus } = useDocking();
    const [pending, setPending] = useState<PendingGate | null>(null);
    const asyncResolveRef = useRef<(() => void) | null>(null);

    const needsGate = dockingUiVisible && isDocked;

    const settleAsync = useCallback(() => {
        const resolve = asyncResolveRef.current;
        asyncResolveRef.current = null;
        resolve?.();
    }, []);

    const runWithDockedGate = useCallback(
        (proceed: () => void, body?: string) => {
            if (!needsGate) {
                proceed();
                return;
            }
            setPending({
                body: body ?? t("docking.chargingBody"),
                proceed,
            });
        },
        [needsGate, t],
    );

    const runWithDockedGateAsync = useCallback(
        (proceed: () => Promise<void>, body?: string): Promise<void> => {
            if (!needsGate) {
                return proceed();
            }
            return new Promise((resolve) => {
                asyncResolveRef.current = resolve;
                setPending({
                    body: body ?? t("docking.chargingBody"),
                    proceed: () => {
                        setPending(null);
                        void proceed().finally(() => {
                            settleAsync();
                        });
                    },
                });
            });
        },
        [needsGate, settleAsync, t],
    );

    const closeGate = useCallback(() => {
        setPending(null);
        settleAsync();
    }, [settleAsync]);

    const confirmProceed = useCallback(() => {
        const action = pending?.proceed;
        setPending(null);
        action?.();
    }, [pending]);

    const confirmUndock = useCallback(() => {
        closeGate();
        triggerDocking();
    }, [closeGate, triggerDocking]);

    const dockedGateModal: ReactNode = (
        <DockedActionGateModal
            visible={pending != null}
            body={pending?.body}
            onCancel={closeGate}
            onProceed={confirmProceed}
            onUndock={confirmUndock}
            undockDisabled={actionStatus === "active"}
        />
    );

    return {
        needsGate,
        runWithDockedGate,
        runWithDockedGateAsync,
        dockedGateModal,
    };
}
